import {
  Banner,
  BannerDescription,
  Button,
  Code,
  Form,
  FormLayout,
  FormLayoutGroup,
  Heading,
  InlineStack,
  Link,
  TextArea,
  TextField,
  TextLabel,
  Toggle,
} from '@customerio/pluma-components/react';
import { Controller, useWatch, useZodForm } from '@/components/form';
import type { SqlDatabaseType, SqlImportDatabase } from '@/services/db/sql-import-database/entity';
import { isEmptyString } from '@/utils/string';
import { databaseFormDefaults, databaseFormSchema, usesPort, usesSsl, type DatabaseFormValues } from '../database-form';
import type { DatabaseSaveResult } from '../use-database-step';

const KEY_FILE_HELP_URL = 'https://cloud.google.com/iam/docs/service-account-creds';
const PEM_PLACEHOLDER = [
  '-----BEGIN PRIVATE KEY-----',
  'MIIEvgIBADANBgkqhkiG9w0BAQEFAASCBKgwggSkAgEAAoIBAQC7VJTUt9Us8cKB',
  'wlTy4iYKUfzKKEFWj7EtJsrfKfxnN5XbTSYGPLQXu0lHB7F7lG8hLjQQYHjxnP5',
  '-----END PRIVATE KEY-----',
].join('\n');

type RegisterPluma = ReturnType<typeof useZodForm<DatabaseFormValues>>['registerPluma'];

type FieldsProps = {
  register: RegisterPluma;
  isReadOnly: boolean;
  /** Shown under a secret when editing: the server keeps it but never returns it. */
  secretHint: string | undefined;
};

const SECRET_HINT = 'Stored securely and not shown. Enter it again to save your changes.';

function BigQueryFields({ register, isReadOnly, secretHint }: FieldsProps) {
  return (
    <>
      <TextField
        label="Project ID"
        autoFocus
        isDisabled={isReadOnly}
        data-test-create-sql-import-input="project-id"
        {...register('project_id')}
      />
      <div>
        <InlineStack alignBlock="end" alignInline="space-between" mb="050">
          <TextLabel id="key-file-contents-label" htmlFor="key-file-contents">
            Key file contents (JSON format)
          </TextLabel>
          <Link icon="help" variant="secondary" isUnderlined isExternal href={KEY_FILE_HELP_URL}>
            How do I get a key file?
          </Link>
        </InlineStack>
        <TextArea
          id="key-file-contents"
          ariaLabelledby="key-file-contents-label"
          rows={5}
          description={secretHint}
          isDisabled={isReadOnly}
          data-test-create-sql-import-input="key-file-contents"
          {...register('password')}
        />
      </div>
    </>
  );
}

type TcpFieldsProps = FieldsProps & {
  type: SqlDatabaseType;
};

function HostField({ type, register, isReadOnly }: Omit<TcpFieldsProps, 'secretHint'>) {
  const isSnowflake = type === 'snowflake';
  return (
    <TextField
      label={isSnowflake ? 'Account name' : 'Host'}
      placeholder={isSnowflake ? 'account' : undefined}
      isDisabled={isReadOnly}
      data-test-create-sql-import-input="database-host"
      {...register('host')}
    />
  );
}

function TcpFields({ type, register, isReadOnly, secretHint }: TcpFieldsProps) {
  const isSnowflake = type === 'snowflake';
  return (
    <>
      <TextField
        label="Database name"
        autoFocus
        isDisabled={isReadOnly}
        data-test-create-sql-import-input="database-name"
        {...register('name')}
      />
      {usesPort(type) ? (
        <FormLayoutGroup>
          <HostField type={type} register={register} isReadOnly={isReadOnly} />
          <TextField
            label="Port"
            placeholder={type === 'mysql' ? '3306' : '5432'}
            inputMode="numeric"
            isDisabled={isReadOnly}
            data-test-create-sql-import-input="database-port"
            {...register('port')}
          />
        </FormLayoutGroup>
      ) : (
        <HostField type={type} register={register} isReadOnly={isReadOnly} />
      )}
      {isSnowflake ? (
        <>
          <TextField
            label="Warehouse"
            autoComplete="off"
            isDisabled={isReadOnly}
            data-test-create-sql-import-input="database-warehouse"
            {...register('warehouse')}
          />
          <TextField
            label="Role"
            autoComplete="off"
            isDisabled={isReadOnly}
            data-test-create-sql-import-input="database-role"
            {...register('role')}
          />
        </>
      ) : null}
      <TextField
        label="Username"
        autoComplete="off"
        isDisabled={isReadOnly}
        data-test-create-sql-import-input="database-username"
        {...register('username')}
      />
      {isSnowflake ? (
        <TextArea
          label="Private key"
          rows={5}
          placeholder={PEM_PLACEHOLDER}
          description={secretHint}
          isDisabled={isReadOnly}
          data-test-create-sql-import-input="database-pem-private-key"
          {...register('pem_private_key')}
        />
      ) : (
        <TextField
          label="Password"
          type="password"
          autoComplete="off"
          description={secretHint}
          isDisabled={isReadOnly}
          data-test-create-sql-import-input="database-password"
          {...register('password')}
        />
      )}
    </>
  );
}

type SslFieldsProps = FieldsProps & {
  form: ReturnType<typeof useZodForm<DatabaseFormValues>>;
  type: SqlDatabaseType;
  sqlServerIp: string;
};

function SslFields({ form, type, register, isReadOnly, secretHint, sqlServerIp }: SslFieldsProps) {
  const ssl = useWatch({ control: form.control, name: 'ssl' });
  return (
    <>
      <Controller
        name="ssl"
        control={form.control}
        render={({ field }) => (
          <Toggle
            label="SSL connection"
            isChecked={field.value}
            isDisabled={isReadOnly}
            data-test-create-sql-import-ssl-switch
            onChange={(event) => {
              field.onChange(event.target.checked);
              if (!event.target.checked && type === 'mysql') {
                form.setValue('ssl_server_ca', '');
                form.setValue('ssl_client_key', '');
                form.setValue('ssl_client_cert', '');
              }
            }}
          />
        )}
      />
      {ssl ? (
        <>
          <TextArea
            label="SSL server certificate authority"
            rows={2}
            description={secretHint}
            isDisabled={isReadOnly}
            data-test-create-sql-import-input="ssl-server-ca"
            {...register('ssl_server_ca')}
          />
          <TextArea
            label="SSL client key"
            rows={2}
            description={secretHint}
            isDisabled={isReadOnly}
            data-test-create-sql-import-input="ssl-client-key"
            {...register('ssl_client_key')}
          />
          <TextArea
            label="SSL client certificate"
            rows={2}
            description={secretHint}
            isDisabled={isReadOnly}
            data-test-create-sql-import-input="ssl-client-cert"
            {...register('ssl_client_cert')}
          />
        </>
      ) : null}
      <Banner icon="lock-secure" variant="default">
        <BannerDescription>
          Update your firewall to allow connections to your database from Customer.io IPs:{' '}
          <Code data-test-sql-server-whitelist-address>{sqlServerIp}</Code>
        </BannerDescription>
      </Banner>
    </>
  );
}

type DatabaseFormProps = {
  type: SqlDatabaseType;
  /** The database being edited, or undefined for a new one. */
  database: SqlImportDatabase | undefined;
  /** Whether the customer can go back to the list instead of saving. */
  canCancel: boolean;
  isReadOnly: boolean;
  isSaving: boolean;
  /** The backend could not connect with the last submitted settings. */
  connectionError: string | null;
  sqlServerIp: string;
  onCancel: () => void;
  onSave: (values: DatabaseFormValues) => Promise<DatabaseSaveResult>;
};

/**
 * The connection settings for one database. The fields follow the engine: host and port for the
 * TCP databases, an account and key pair for Snowflake, a project and key file for BigQuery.
 */
export function DatabaseForm({
  type,
  database,
  canCancel,
  isReadOnly,
  isSaving,
  connectionError,
  sqlServerIp,
  onCancel,
  onSave,
}: DatabaseFormProps) {
  const isNew = database == null;
  const form = useZodForm<DatabaseFormValues>({
    schema: databaseFormSchema(type),
    mode: 'onSubmit',
    defaultValues: databaseFormDefaults(database),
  });
  const register = form.registerPluma;
  const secretHint = isNew ? undefined : SECRET_HINT;

  const submit = form.handleSubmit(async (values) => {
    const result = await onSave(values);
    if (result.status === 'invalid') {
      for (const { field, message } of result.errors) {
        form.setError(field, { message });
      }
    }
  });

  return (
    <Form onSubmit={submit} data-test-create-sql-import-database-form>
      <Heading level="3" color="bold" mb="100">
        {isNew ? 'Add database details' : 'Edit database'}
      </Heading>
      <FormLayout>
        {type === 'bigquery' ? (
          <BigQueryFields register={register} isReadOnly={isReadOnly} secretHint={secretHint} />
        ) : (
          <TcpFields type={type} register={register} isReadOnly={isReadOnly} secretHint={secretHint} />
        )}
        {usesSsl(type) ? (
          <SslFields
            form={form}
            type={type}
            register={register}
            isReadOnly={isReadOnly}
            secretHint={secretHint}
            sqlServerIp={sqlServerIp}
          />
        ) : null}
      </FormLayout>
      <InlineStack alignBlock="stretch" alignInline="end" mt="200" gap="100">
        {canCancel ? (
          <Button type="button" variant="tertiary" onClick={onCancel} data-test-sql-import-button="cancel">
            Cancel
          </Button>
        ) : null}
        <Button
          type="submit"
          variant="primary"
          isDisabled={isReadOnly}
          isLoading={isSaving}
          data-test-sql-import-button="save-database"
        >
          {isNew ? 'Add database' : 'Update database'}
        </Button>
      </InlineStack>
      {isEmptyString(connectionError) ? null : (
        <Banner
          title="Your database couldn't be saved"
          variant="critical"
          mt="200"
          data-test-create-sql-import-alert="save-error"
        >
          <BannerDescription>{connectionError}</BannerDescription>
        </Banner>
      )}
    </Form>
  );
}
